import React, { useCallback, useState } from "react";
import { UIFlightData, UIServiceError } from "@flights/types";
import { SheetContainer } from "@bookingcom/bui-react";

import { FlightDetailsInner, FlightDetailsInnerDesktop } from "../FlightDetailsInner";
import { clientFetch } from "@flights/client-fetch";
import useUserAgent from "../../../hooks/useUserAgent";
import { useHistory } from "react-router-dom";
import useClientCachedURL from "../../../hooks/useClientCachedURL";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { actions as flightDetailsActions } from "../../../store/flightDetails/actions";
// eslint-disable-next-line import/no-deprecated
import { useActions } from "../../../store";
import useAlternativeFaresParams from "../../../hooks/useAlternativeFaresParams";
import { flightsLocalStore } from "@flights/web-api-utils-universal";
import { CURRENT_ORDER_ID_KEY } from "../../../constants";
import AlternativeFaresCarousel from "./AlternativeFaresCarousel";
import flights_alternative_fares_for_price_change_and_sold_out_errors_web, {
  useTrackSelectedAlternativeFare
} from "../../../utils/experiments/order/flights_alternative_fares_for_price_change_and_sold_out_errors_web";
import { PAYMENT_CAPTURED_PARAM } from "../../../component-controllers/CheckoutPaymentController";
interface AlternativeFaresProps {
  alternativeFares?: UIFlightData[];
  isLoading?: boolean;
}

const AlternativeFares: React.FunctionComponent<AlternativeFaresProps> = ({
  alternativeFares = [],
  isLoading
}: AlternativeFaresProps) => {
  // eslint-disable-next-line import/no-deprecated
  const actions = useActions(flightDetailsActions);
  const [selectedFlight, setSelectedFlight] = useState<UIFlightData | undefined>(undefined);
  const [flightDetailsLoading, setFlightDetailsLoading] = useState<boolean>(false);
  const [showFlightDetails, setShowFlightDetails] = useState<boolean>(false);

  const { isMobile } = useUserAgent();

  const { getLastSuccessfulSearchUrl } = useClientCachedURL();
  const lastSearchUrl = getLastSuccessfulSearchUrl();

  const history = useHistory();
  const i18n = useI18n();

  const { getAlternativeFaresParams } = useAlternativeFaresParams();
  const { setSelectedAlternativeFare } = useTrackSelectedAlternativeFare();

  const onFlightCardClick = async (flight: UIFlightData) => {
    actions.fetch();

    setSelectedFlight(flight);
    setShowFlightDetails(true);
    setFlightDetailsLoading(true);

    flights_alternative_fares_for_price_change_and_sold_out_errors_web.goals.click_select_on_alt_fares();

    const orderId = flightsLocalStore.get(CURRENT_ORDER_ID_KEY);

    void clientFetch("/api/order/track-alternative-fares-event", {
      method: "POST",
      body: JSON.stringify({
        eventType: "ALTERNATIVE_FARE_VIEWED",
        orderId,
        selectedOffer: flight
      }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json"
      }
    });

    const searchParams = new URLSearchParams(location.search);
    const fdParams = getAlternativeFaresParams();

    if (fdParams?.excludedAncillaries) {
      searchParams.set("excludedAncillaries", fdParams.excludedAncillaries);
    }

    if (fdParams?.salesCountry) {
      searchParams.set("salesCountry", fdParams.salesCountry);
    }

    const paymentCaptured = searchParams.get(PAYMENT_CAPTURED_PARAM);
    if (paymentCaptured && paymentCaptured === "1") {
      setSelectedAlternativeFare(flight.token);
    }

    await clientFetch(`/api/flight/${flight.token}?${searchParams.toString()}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" }
    })
      .then((flight: UIFlightData) => {
        setSelectedFlight(flight);
        setFlightDetailsLoading(false);
        actions.fetchSuccess(flight);
      })
      .catch((error: UIServiceError) => {
        actions.fetchError(error);
      });
  };

  const handleOnClose = useCallback(() => {
    setShowFlightDetails(false);
    actions.fetch();
  }, [actions]);

  return (
    <div>
      <AlternativeFaresCarousel
        flights={alternativeFares}
        loading={isLoading}
        onActionClick={() => {
          history.push(lastSearchUrl);
        }}
        onFareClick={(fare) => onFlightCardClick(fare)}
      />

      {/* Flight details modal for desktop */}
      {showFlightDetails && selectedFlight && !isMobile && (
        <FlightDetailsInnerDesktop
          key={selectedFlight?.token}
          flight={selectedFlight}
          flightToken={selectedFlight?.token}
          onClose={handleOnClose}
          onErrorClose={() => {}}
          fetchStatus={flightDetailsLoading ? "loading" : "success"}
          closeAriaLabel={i18n.trans(t("a11y_flights_failed_alternative_details_modal_close"))}
          alternativeFaresFlow={true}
        />
      )}

      {/* Flight details sheet container for mobile*/}
      {showFlightDetails && selectedFlight && isMobile && (
        <SheetContainer active={true} position="fullScreen" hideClose={true} fill={true}>
          <FlightDetailsInner
            key={selectedFlight?.token}
            flight={selectedFlight}
            flightToken={selectedFlight?.token}
            onClose={handleOnClose}
            onErrorClose={() => {}}
            fetchStatus={flightDetailsLoading ? "loading" : "success"}
            closeAriaLabel={i18n.trans(t("a11y_flights_failed_alternative_details_modal_close"))}
            alternativeFaresFlow={true}
          />
        </SheetContainer>
      )}
    </div>
  );
};

export { AlternativeFares };
